import { Meteor } from 'meteor/meteor';
import { Mongo } from 'meteor/mongo';
import { check, Match } from 'meteor/check';
import { addError } from '/imports/utils';

export const Errors = {
  required: 'required',
  mustBeString: 'mustBeString',
  mustBeNumber: 'mustBeNumber',
  mustBeArray: 'mustBeArray',
  doesNotExist: 'doesNotExist',
  mustBeBeforeEndTime: 'mustBeBeforeEndTime',
  mustBeAfterStartTime: 'mustBeAfterStartTime',
  errorCreatingUser: 'errorCreatingUser',
  staffAlreadyExists: 'staffAlreadyExists',
  validationError: 'validationError',
  mustBeUnique: 'mustBeUnique',
  failedRegex: 'failedRegex',
  lessThanMin: 'lessThanMin',
  invalid: 'invalid',
};

export const validateExists = async <T>(
  errors: { [key: string]: string[] },
  key: string,
  collection: Mongo.Collection<any>,
  query: string | Mongo.Query<T>
) => {
  if (Meteor.isClient) return true;
  if (!await collection.findOneAsync(query)) return addError(errors, key as string, Errors.doesNotExist);
  return true;
};

export const validateArray = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.mustBeArray);
  if (!Array.isArray(value)) return addError(errors, key as string, Errors.mustBeArray);
  return true;
};

export const validateString = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean,
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  if (typeof value !== 'string') return addError(errors, key as string, Errors.mustBeString);
  if (options?.required && value.trim().length === 0) return addError(errors, key as string, Errors.required);
  return true;
};

export const validateNumber = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean,
    min?: number,
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  if (typeof value !== 'number') return addError(errors, key as string, Errors.mustBeNumber);
  if (typeof options?.min === 'number' && value < options.min) return addError(errors, key as string, Errors.lessThanMin);
  return true;
};

export const validateDate = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  if (!(value instanceof Date)) return addError(errors, key as string, Errors.invalid);
  return true;
};

export const validateUnique = async <T>(
  errors: { [key: string]: string[] },
  obj: any,
  key: keyof T,
  collection: Mongo.Collection<any>
) => {
  const value = obj[key];
  if (value === undefined) return true;
  if (obj._id) {
    const query = { [key]: value, _id: { $ne: obj._id } };
    return await collection.findOneAsync(query) ? addError(errors, key as string, Errors.mustBeUnique) : true;
  }
  else {
    const query = { [key]: value };
    return await collection.findOneAsync(query) ? addError(errors, key as string, Errors.mustBeUnique) : true;
  }
};

export const validateRegex = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  re: RegExp
) => {
  const value = obj[key];
  if (typeof value === 'string') {
    if (!re.test(value)) return addError(errors, key as string, Errors.failedRegex);
  }
  return true;
};

export const validateTrue = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
) => {
  const value = obj[key];
  return typeof value === 'boolean' && !!value ? true : addError(errors, key as string, Errors.required);
};

export const validateBoolean = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
) => {
  const value = obj[key];
  return value === undefined || value === true || value === false ? true : addError(errors, key as string, Errors.required);
};

export const validateAddress = <T>(
  errors: { [key: string]: string[] },
  obj: T,
  key: keyof T,
  options?: {
    required?: boolean,
  }
) => {
  const value = obj[key];
  if (!options?.required && (value === undefined)) return true;
  if (options?.required && value === undefined) return addError(errors, key as string, Errors.required);
  try {
    check(value, {
      number: Match.Maybe(String),
      route: Match.Maybe(String),
      city: String,
      state: String,
      stateName: String,
      country: String,
      countryName: String,
      zipcode: Match.Maybe(String),
      line: String,
      line2: Match.Maybe(String),
      formatted: String,
      location: { lat: Number, lng: Number },
    });
  }
  catch (error) {
    console.log('error', error);
    addError(errors, key as string, Errors.invalid);
  }
  return true;
};